import React from 'react';
import type { CatalogV2Offer } from '../../../types/catalogV2Product';
import { trackRetailerProductPageOpen } from '../../tracking/trackRetailerProductPageOpen';
import { formatMoney, retailerLogoUrl } from './helpers';
import { INK } from './constants';

/**
 * Cross-retailer price comparison (Figma "Store Card" 5031:3593). Each row shows
 * a cross-retailer offer: logo + name on the left and a "View • $price" pill
 * linking to that retailer's product page on the right. Driven directly by the
 * product's V2 `offers` (primary excluded upstream).
 *
 * These are retailer exits too, so they emit the same
 * `webApp:organic:openRetailerProductPage` as the primary "View". They deliberately
 * send NO `availabilityStatus`: offers carry no availability flag, and the product's
 * own `isAvailable` describes the PRIMARY listing, not this retailer's. Claiming
 * `InStock` here would fabricate external orders, so these clicks are tracked but
 * do not qualify — which is the correct outcome for an unverifiable listing.
 */
export function RetailerComparison({ products }: { products: CatalogV2Offer[] }) {
  const longestPriceLabel = products.reduce((longest, product) => {
    const label = formatMoney(product.retailPrice ?? 0);
    return label.length > longest.length ? label : longest;
  }, '');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '16px' }}>
      {products.map((product) => {
        const logo = retailerLogoUrl(product.retailerId);
        const name = product.retailerName || '';
        return (
          <div
            key={product.stacklineSku}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              {logo && (
                <img
                  src={logo}
                  alt=""
                  aria-hidden="true"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                  }}
                  style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'contain', flexShrink: 0 }}
                />
              )}
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: INK,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {name}
              </span>
            </div>
            <a
              href={product.retailerProductPageUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() =>
                trackRetailerProductPageOpen({
                  stacklineSku: product.stacklineSku,
                  retailerSku: product.retailerSku,
                  retailerId: product.retailerId,
                  retailerName: product.retailerName,
                  retailPrice: product.retailPrice,
                  retailerProductPageUrl: product.retailerProductPageUrl,
                })
              }
              style={{
                display: 'inline-grid',
                placeItems: 'center',
                padding: '8px 16px',
                borderRadius: 100,
                border: '1px solid #DCDDDC',
                background: '#fff',
                color: INK,
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '20px',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              <span aria-hidden="true" style={{ gridArea: '1 / 1', visibility: 'hidden' }}>
                View • {longestPriceLabel}
              </span>
              <span style={{ gridArea: '1 / 1' }}>View • {formatMoney(product.retailPrice ?? 0)}</span>
            </a>
          </div>
        );
      })}
    </div>
  );
}
